import { toast } from 'sonner';
import { logger } from '@/utils/logger';

type FeedbackType = 'success' | 'error' | 'warning' | 'info' | 'loading';

interface FeedbackOptions {
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  dismissible?: boolean;
  position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
}

interface UserFeedback {
  id: string;
  type: FeedbackType;
  message: string;
  timestamp: number;
  dismissed?: boolean;
  context?: Record<string, any>;
}

class UserFeedbackService {
  private feedbackHistory: UserFeedback[] = [];
  private activeFeedback: Map<string, string> = new Map(); // key -> toast id

  showFeedback(
    type: FeedbackType,
    message: string,
    options: FeedbackOptions = {},
    context?: Record<string, any>
  ): string {
    const feedbackId = this.generateFeedbackId();
    
    const feedback: UserFeedback = {
      id: feedbackId,
      type,
      message,
      timestamp: Date.now(),
      context
    };
    
    this.feedbackHistory.push(feedback);
    
    // Keep only last 50 feedback items
    if (this.feedbackHistory.length > 50) {
      this.feedbackHistory = this.feedbackHistory.slice(-50);
    }
    
    let toastId: string | number;
    
    switch (type) {
      case 'success':
        toastId = toast.success(message, {
          duration: options.duration || 4000,
          action: options.action,
          dismissible: options.dismissible !== false
        });
        break;
        
      case 'error':
        toastId = toast.error(message, {
          duration: options.duration || 6000,
          action: options.action,
          dismissible: options.dismissible !== false
        });
        break;
        
      case 'warning':
        toastId = toast.warning(message, {
          duration: options.duration || 5000,
          action: options.action,
          dismissible: options.dismissible !== false
        });
        break;
        
      case 'info':
        toastId = toast.info(message, {
          duration: options.duration || 4000,
          action: options.action,
          dismissible: options.dismissible !== false
        });
        break;
        
      case 'loading':
        toastId = toast.loading(message, {
          duration: options.duration || Infinity,
          dismissible: options.dismissible !== false
        });
        break;
        
      default:
        toastId = toast(message, {
          duration: options.duration || 4000,
          action: options.action,
          dismissible: options.dismissible !== false
        });
    }
    
    this.activeFeedback.set(feedbackId, String(toastId));
    
    // Log feedback for analytics
    console.log(`User Feedback: ${type}`, { message, context });
    
    return feedbackId;
  }

  updateFeedback(feedbackId: string, message: string, type?: FeedbackType) {
    const toastId = this.activeFeedback.get(feedbackId);
    if (toastId) {
      toast.dismiss(toastId);
      
      if (type) {
        this.showFeedback(type, message);
      } else {
        toast(message);
      }
    }
  }

  dismissFeedback(feedbackId: string) {
    const toastId = this.activeFeedback.get(feedbackId);
    if (toastId) {
      toast.dismiss(toastId);
      this.activeFeedback.delete(feedbackId);
      
      // Mark as dismissed in history
      const feedback = this.feedbackHistory.find(f => f.id === feedbackId);
      if (feedback) {
        feedback.dismissed = true;
      }
    }
  }

  dismissAll() {
    toast.dismiss();
    this.activeFeedback.clear();
  }

  // Convenience methods
  success(message: string, options?: FeedbackOptions, context?: Record<string, any>) {
    return this.showFeedback('success', message, options, context);
  }

  error(message: string, options?: FeedbackOptions, context?: Record<string, any>) {
    return this.showFeedback('error', message, options, context);
  }

  warning(message: string, options?: FeedbackOptions, context?: Record<string, any>) {
    return this.showFeedback('warning', message, options, context);
  }

  info(message: string, options?: FeedbackOptions, context?: Record<string, any>) {
    return this.showFeedback('info', message, options, context);
  }

  loading(message: string, options?: FeedbackOptions, context?: Record<string, any>) {
    return this.showFeedback('loading', message, options, context);
  }

  // Progress feedback for long operations
  async withProgress<T>(
    operation: (updateProgress: (message: string, progress?: number) => void) => Promise<T>,
    initialMessage: string = 'Processing...'
  ): Promise<T> {
    const loadingId = this.loading(initialMessage);
    
    try {
      const result = await operation((message: string, progress?: number) => {
        const progressText = progress !== undefined ? ` (${Math.round(progress)}%)` : '';
        this.updateFeedback(loadingId, message + progressText, 'loading');
      });
      
      this.dismissFeedback(loadingId);
      return result;
    } catch (error) {
      this.dismissFeedback(loadingId);
      this.error(error instanceof Error ? error.message : 'Operation failed');
      throw error;
    }
  }

  getFeedbackHistory(): UserFeedback[] {
    return [...this.feedbackHistory];
  }

  clearHistory() {
    this.feedbackHistory.length = 0;
  }

  private generateFeedbackId(): string {
    return `feedback-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

export const feedbackService = new UserFeedbackService();
export type { FeedbackType, FeedbackOptions, UserFeedback };
